import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Alert,
    Grid,
    Typography,
    CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useGetBanksForSelect from "../../../../hooks/banks/useGetBanksForSelect";
import useGetCurrenciesForSelect from "../../../../hooks/currencies/useGetCurrenciesForSelect";
import useCreateProgramInvoice from "../../../../hooks/invoices/useCreateProgramInvoice";
import useUpdateInvoice from "../../../../hooks/invoices/useUpdateInvoice";
import useConvertInvoiceCurrency from "../../../../hooks/invoices/useConvertInvoiceCurrency";
import useGetProgramInvoice from "../../../../hooks/invoices/useGetProgramInvoice";
import DateInput from "../../../inputs/DateInput";
import dayjs from "dayjs";

const CreateEditInvoiceModal = ({ open, onClose, program, existingInvoice }) => {
    const { i18n } = useTranslation();
    const lang = i18n.language;

    const { data: banksData, isLoading: isLoadingBanks } = useGetBanksForSelect();
    const banks = banksData?.data || banksData?.banks || [];

    const { rawData: currenciesData, isLoading: isLoadingCurrencies } = useGetCurrenciesForSelect();
    const currencies = currenciesData?.data || (Array.isArray(currenciesData) ? currenciesData : []);

    const { createProgramInvoiceMutate, isPending: isCreating } = useCreateProgramInvoice();
    const { updateInvoiceMutate, isPending: isUpdating } = useUpdateInvoice();
    const { convertInvoiceCurrencyMutate, isPending: isConverting } = useConvertInvoiceCurrency();

    const isEditing = !!existingInvoice;

    // Fetch full invoice details if editing
    const { data: fullInvoiceData, isLoading: isLoadingInvoice } = useGetProgramInvoice(isEditing ? program?.id : null);
    const fullInvoice = fullInvoiceData?.data || null;

    const isPending = isCreating || isUpdating || isConverting;

    const defaultDueDate = program?.start_date ? dayjs(program.start_date).subtract(3, "day").format("YYYY-MM-DD") : "";

    const [formData, setFormData] = useState({
        invoice_type: "agent",
        billed_to_name: { ar: "", en: "" },
        billed_to_customer: { ar: "", en: "" },
        payment_due_by: defaultDueDate,
        bank_id: "",
        display_currency_id: "",
        payment_remark: { ar: "", en: "" },
    });

    useEffect(() => {
        if (open) {
            if (isEditing && fullInvoice) {
                const custSource = fullInvoice.billed_to_customer || fullInvoice.billed_to_name;
                setFormData({
                    invoice_type: fullInvoice.invoice_type || "agent",
                    billed_to_name: {
                        ar: fullInvoice.billed_to_name?.ar || (typeof fullInvoice.billed_to_name === "string" ? fullInvoice.billed_to_name : "") || "",
                        en: fullInvoice.billed_to_name?.en || "",
                    },
                    billed_to_customer: {
                        ar: custSource?.ar || (typeof custSource === "string" ? custSource : "") || "",
                        en: custSource?.en || "",
                    },
                    payment_due_by: fullInvoice.payment_due_by || defaultDueDate,
                    bank_id: fullInvoice.bank_id || "",
                    display_currency_id: fullInvoice.effective_currency?.id || fullInvoice.invoice_currency?.id || program?.currency_id || "",
                    payment_remark: {
                        ar: fullInvoice.payment_remark?.ar || "",
                        en: fullInvoice.payment_remark?.en || "",
                    },
                });
            } else if (!isEditing) {
                const defaultCustName = program?.description || { ar: "", en: "" };
                setFormData({
                    invoice_type: "agent",
                    billed_to_name: { ar: "", en: "" },
                    billed_to_customer: {
                        ar: defaultCustName?.ar || (typeof defaultCustName === "string" ? defaultCustName : "") || "",
                        en: defaultCustName?.en || "",
                    },
                    payment_due_by: defaultDueDate,
                    bank_id: "",
                    display_currency_id: program?.currency_id || "",
                    payment_remark: { ar: "", en: "" },
                });
            }
        }
    }, [open, isEditing, fullInvoice, defaultDueDate, program]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleNestedChange = (field, langKey, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: { ...prev[field], [langKey]: value },
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            invoice_type: formData.invoice_type,
            payment_due_by: formData.payment_due_by,
            bank_id: formData.bank_id ? parseInt(formData.bank_id, 10) : "",
            payment_remark: formData.payment_remark,
        };

        if (!isEditing && formData.display_currency_id) {
            payload.display_currency_id = parseInt(formData.display_currency_id, 10);
        }

        if (formData.invoice_type === "customer") {
            payload.billed_to_customer = formData.billed_to_customer;
            payload.billed_to_name = formData.billed_to_customer;
        }

        if (isEditing) {
            updateInvoiceMutate(
                { id: existingInvoice.id, data: payload },
                {
                    onSuccess: () => {
                        const originalCurrencyId = fullInvoice.effective_currency?.id || fullInvoice.invoice_currency?.id || program?.currency_id;
                        if (formData.display_currency_id && String(formData.display_currency_id) !== String(originalCurrencyId)) {
                            convertInvoiceCurrencyMutate({
                                invoiceId: existingInvoice.id,
                                currencyId: parseInt(formData.display_currency_id, 10)
                            }, {
                                onSuccess: () => onClose()
                            });
                        } else {
                            onClose();
                        }
                    }
                }
            );
        } else {
            createProgramInvoiceMutate(
                { programId: program.id, data: payload },
                { onSuccess: () => onClose() }
            );
        }
    };

    // Check for currency mismatch
    const selectedBankId = formData.bank_id;
    const selectedBank = banks.find((b) => String(b.value || b.id || b.bank_id) === String(selectedBankId));
    const bankCurrencyId = selectedBank?.currency_id || selectedBank?.currency?.id;
    const programCurrencyId = program?.currency_id || program?.currency?.id;
    const effectiveCurrencyId = formData.display_currency_id || programCurrencyId;
    const isCurrencyMismatch = selectedBank && String(bankCurrencyId) !== String(effectiveCurrencyId);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isEditing
                    ? (lang === "ar" ? "تعديل الفاتورة" : "Edit Invoice")
                    : (lang === "ar" ? "إنشاء فاتورة" : "Create Invoice")
                }
            </DialogTitle>
            {isEditing && isLoadingInvoice ? (
                <DialogContent dividers>
                    <Grid container justifyContent="center" sx={{ py: 3 }}>
                        <CircularProgress />
                    </Grid>
                </DialogContent>
            ) : (
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label={lang === "ar" ? "نوع الفاتورة" : "Invoice Type"}
                                    name="invoice_type"
                                    value={formData.invoice_type}
                                    onChange={handleChange}
                                    required
                                >
                                    <MenuItem value="agent">{lang === "ar" ? "وكيل" : "Agent"}</MenuItem>
                                    <MenuItem value="customer">{lang === "ar" ? "عميل" : "Customer"}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <DateInput
                                    label={lang === "ar" ? "تاريخ الاستحقاق" : "Payment Due By"}
                                    value={formData.payment_due_by}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            payment_due_by: e.target.value ? dayjs(e.target.value).format("YYYY-MM-DD") : "",
                                        }))
                                    }
                                    required
                                />
                            </Grid>

                            {formData.invoice_type === "customer" && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label={lang === "ar" ? "اسم العميل (عربي)" : "Customer Name (Ar)"}
                                            value={formData.billed_to_customer.ar}
                                            onChange={(e) => handleNestedChange("billed_to_customer", "ar", e.target.value)}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label={lang === "ar" ? "اسم العميل (إنجليزي)" : "Customer Name (En)"}
                                            value={formData.billed_to_customer.en}
                                            onChange={(e) => handleNestedChange("billed_to_customer", "en", e.target.value)}
                                            required
                                            dir="ltr"
                                        />
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label={lang === "ar" ? "البنك" : "Bank"}
                                    name="bank_id"
                                    value={formData.bank_id ? String(formData.bank_id) : ""}
                                    onChange={handleChange}
                                    required
                                    disabled={isLoadingBanks}
                                >
                                    {banks.map((bank) => (
                                        <MenuItem key={bank.value || bank.id || bank.bank_id} value={String(bank.value || bank.id || bank.bank_id)}>
                                            {lang === "ar"
                                                ? (bank.bank_name?.ar || bank.bank_name?.en || bank.name?.ar || bank.name?.en || "")
                                                : (bank.bank_name?.en || bank.bank_name?.ar || bank.name?.en || bank.name?.ar || "")}
                                            {bank.currency ? ` (${bank.currency.code})` : ""}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label={lang === "ar" ? "عملة العرض" : "Display Currency"}
                                    name="display_currency_id"
                                    value={formData.display_currency_id ? String(formData.display_currency_id) : ""}
                                    onChange={handleChange}
                                    disabled={isLoadingCurrencies || (isEditing && fullInvoice?.state === 'settled')}
                                >
                                    {currencies.map((currency) => (
                                        <MenuItem key={currency.id} value={String(currency.id)}>
                                            {lang === "ar" ? currency.name?.ar : currency.name?.en} ({currency.code})
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {isCurrencyMismatch && (
                                <Grid item xs={12}>
                                    <Alert severity="warning">
                                        {lang === "ar"
                                            ? "تنبيه: عملة حساب البنك تختلف عن عملة العرض المحددة للفاتورة."
                                            : "Warning: The bank account's currency differs from the selected display currency."
                                        }
                                    </Alert>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {lang === "ar" ? "ملاحظات الدفع" : "Payment Remarks"}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    label={lang === "ar" ? "عربي" : "Arabic"}
                                    value={formData.payment_remark.ar}
                                    onChange={(e) => handleNestedChange("payment_remark", "ar", e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    label={lang === "ar" ? "إنجليزي" : "English"}
                                    value={formData.payment_remark.en}
                                    onChange={(e) => handleNestedChange("payment_remark", "en", e.target.value)}
                                    dir="ltr"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose} color="inherit">
                            {lang === "ar" ? "إلغاء" : "Cancel"}
                        </Button>
                        <Button type="submit" variant="contained" color="primary" disabled={isPending}>
                            {isPending
                                ? (lang === "ar" ? "جاري الحفظ..." : "Saving...")
                                : (lang === "ar" ? "حفظ" : "Save")
                            }
                        </Button>
                    </DialogActions>
                </form>
            )}
        </Dialog>
    );
};

export default CreateEditInvoiceModal;
